import { logger } from './logger';
import { env } from './env';
import { supabase } from './supabase';
import { withRetry } from '../utils/retry';
import { modelConfig } from './model-config';

export interface GeneratedResource {
  id: string;
  title: string;
  url: string;
  description: string;
  subcategory: string;
  keywords: string[];
  quality_score: number;
  is_verified: boolean;
  trade_type?: string;
}

interface ResourceGenerationParams {
  messageText: string;
  conversationHistory?: Array<{ role: string; content: string }>;
  topicId?: string;
  limit?: number;
  topicCategory?: string;
}

interface EdgeFunctionResponse {
  content: string;
  error?: string;
}

class AIResourceGenerator {
  private static instance: AIResourceGenerator;
  private sessionCache: Map<string, { resources: GeneratedResource[]; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 10 * 60 * 1000;

  private constructor() {}

  public static getInstance(): AIResourceGenerator {
    if (!AIResourceGenerator.instance) {
      AIResourceGenerator.instance = new AIResourceGenerator();
    }
    return AIResourceGenerator.instance;
  }

  private async callEdgeFunction(messages: Array<{ role: string; content: string }>, maxTokens = 1500): Promise<string> {
    const edgeFunctionUrl = `${env.VITE_SUPABASE_URL}/functions/v1/ai-coach-chat`;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        messages,
        model: modelConfig.getModelName(),
        temperature: 0.3,
        max_tokens: maxTokens
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data: EdgeFunctionResponse = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }

    return data.content;
  }

  private buildContextKey(messageText: string, conversationHistory: Array<{ role: string; content: string }>): string {
    const recentMessages = conversationHistory.slice(-3).map(m => m.content).join(' ');
    const contextText = `${recentMessages} ${messageText}`.toLowerCase();
    return contextText.slice(0, 100);
  }

  private extractKeywords(text: string): string[] {
    const lowerText = text.toLowerCase();
    const keywords: string[] = [];

    const careerTerms = [
      'job', 'career', 'work', 'employment', 'apprenticeship', 'trade',
      'electrician', 'plumber', 'hvac', 'carpenter', 'welder', 'mechanic',
      'certification', 'license', 'training', 'school', 'program', 'course'
    ];

    careerTerms.forEach(term => {
      if (lowerText.includes(term)) {
        keywords.push(term);
      }
    });

    return [...new Set(keywords)];
  }

  public async generateResources(params: ResourceGenerationParams): Promise<GeneratedResource[]> {
    const { messageText, conversationHistory = [], topicId, limit = 3, topicCategory } = params;

    try {
      const cacheKey = this.buildContextKey(messageText, conversationHistory);
      const cached = this.sessionCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
        logger.info('Returning cached AI-generated resources');
        return cached.resources.slice(0, limit);
      }

      const keywords = this.extractKeywords(`${conversationHistory.map(m => m.content).join(' ')} ${messageText}`);

      if (topicCategory) {
        keywords.push(topicCategory);
      }

      const isRelevant = keywords.length > 0;
      if (!isRelevant) {
        logger.info('Conversation not relevant for resource generation');
        return [];
      }

      logger.info('Generating AI resources for context:', { keywords, topicCategory });

      const resources = await this.generateResourcesWithAI(messageText, conversationHistory, keywords, limit, topicCategory);

      this.sessionCache.set(cacheKey, {
        resources,
        timestamp: Date.now()
      });

      await this.saveGeneratedResourcesToDB(resources, topicId);

      return resources;
    } catch (error) {
      logger.error('Error generating AI resources:', error);
      return [];
    }
  }

  private async generateResourcesWithAI(
    messageText: string,
    conversationHistory: Array<{ role: string; content: string }>,
    keywords: string[],
    limit: number,
    topicCategory?: string
  ): Promise<GeneratedResource[]> {
    const contextMessages = conversationHistory.slice(-5).map(m => m.content).join('\n');

    const topicContext = topicCategory ? `\n\nTOPIC FOCUS: This conversation is about "${topicCategory}". Prioritize resources specifically relevant to this topic area.` : '';

    const systemPrompt = `You are a resource recommendation expert. Based on the conversation context, generate ${limit} highly relevant, real, and reputable web resources that would help this person.${topicContext}

CRITICAL REQUIREMENTS:
- ONLY recommend REAL websites with VALID URLs that exist
- Focus on official, government, or well-established organizations
- Prioritize .gov, .org, and major established platforms
- For trades/apprenticeships: recommend official apprenticeship programs, trade unions, certification bodies
- For career development: recommend job boards, career resources, government labor sites
- For education: recommend legitimate trade schools, community colleges, certification programs
- For habits: recommend evidence-based habit formation resources, behavior change platforms, accountability tools
- For health: recommend fitness programs, nutrition guides, mental health resources from reputable sources
- For relationships: recommend communication skills resources, relationship counseling directories, social skills guides

CRITICAL: NEVER RECOMMEND RESOURCES ABOUT:
- Game development, game design, game testing, or gaming industry careers
- Streaming platforms (Twitch, YouTube Gaming, etc.)
- Content creation, influencer careers, social media careers
- Esports, pro gaming, or gaming-related work
- Video editing for entertainment/social media
- If the conversation mentions gaming or similar interests, redirect resources toward COMPLETELY DIFFERENT fields like trades, healthcare, emergency services, construction, manufacturing, etc.

FORMAT YOUR RESPONSE EXACTLY AS FOLLOWS:
[RESOURCE_1]
TITLE: [Clear, descriptive title]
URL: [Valid, real URL starting with https://]
DESCRIPTION: [One sentence describing what this resource provides]
SUBCATEGORY: [Specific category like "Electrical Apprenticeship" or "HVAC Certification"]
KEYWORDS: [Comma-separated relevant keywords]
QUALITY: [Number 3-5 representing quality/reputation]
[END_RESOURCE_1]

[RESOURCE_2]
...
[END_RESOURCE_2]

Conversation Context:
${contextMessages}

Current Message: ${messageText}

Detected Topics: ${keywords.join(', ')}

Now generate ${limit} resources:`;

    const content = await withRetry<string>(
      () => this.callEdgeFunction([
        { role: "system", content: systemPrompt },
        { role: "user", content: "Generate the resources now." }
      ], 1500),
      {
        maxAttempts: 2,
        backoffMs: 1000,
        retryableErrors: ['timeout', 'network'],
        onRetry: (attempt, error) => {
          logger.warn(`AI resource generation retry ${attempt}:`, error.message);
        }
      }
    );

    return this.parseResourcesFromResponse(content);
  }

  private parseResourcesFromResponse(content: string): GeneratedResource[] {
    const resources: GeneratedResource[] = [];

    const resourcePattern = /\[RESOURCE_\d+\]([\s\S]*?)\[END_RESOURCE_\d+\]/g;
    const matches = content.matchAll(resourcePattern);

    for (const match of matches) {
      const resourceBlock = match[1];

      const titleMatch = resourceBlock.match(/TITLE:\s*(.+)/);
      const urlMatch = resourceBlock.match(/URL:\s*(https?:\/\/.+)/);
      const descMatch = resourceBlock.match(/DESCRIPTION:\s*(.+)/);
      const subcatMatch = resourceBlock.match(/SUBCATEGORY:\s*(.+)/);
      const keywordsMatch = resourceBlock.match(/KEYWORDS:\s*(.+)/);
      const qualityMatch = resourceBlock.match(/QUALITY:\s*(\d)/);

      if (titleMatch && urlMatch && descMatch) {
        const keywords = keywordsMatch
          ? keywordsMatch[1].split(',').map(k => k.trim()).filter(k => k)
          : [];

        const resource: GeneratedResource = {
          id: `ai-gen-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          title: titleMatch[1].trim(),
          url: urlMatch[1].trim(),
          description: descMatch[1].trim(),
          subcategory: subcatMatch ? subcatMatch[1].trim() : '',
          keywords,
          quality_score: qualityMatch ? parseInt(qualityMatch[1]) : 4,
          is_verified: false,
        };

        resources.push(resource);
      }
    }

    logger.info(`Parsed ${resources.length} resources from AI response`);
    return resources;
  }

  private async saveGeneratedResourcesToDB(resources: GeneratedResource[], _topicId?: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let categoryId: string | null = null;

      const { data: existingCategory } = await supabase
        .from('resource_categories')
        .select('id')
        .eq('name', 'AI Generated Resources')
        .maybeSingle();

      if (existingCategory) {
        categoryId = existingCategory.id;
      } else {
        const { data: newCategory, error: catError } = await supabase
          .from('resource_categories')
          .insert({
            name: 'AI Generated Resources',
            description: 'Dynamically generated resources based on coaching conversations',
            icon: 'Sparkles',
            color_scheme: 'blue',
            sort_order: 10,
            is_active: true
          })
          .select('id')
          .single();

        if (catError) throw catError;
        categoryId = newCategory.id;
      }

      for (const resource of resources) {
        const { data: existing } = await supabase
          .from('coaching_resources')
          .select('id')
          .eq('url', resource.url)
          .maybeSingle();

        if (!existing) {
          await supabase
            .from('coaching_resources')
            .insert({
              category_id: categoryId,
              title: resource.title,
              url: resource.url,
              description: resource.description,
              subcategory: resource.subcategory,
              keywords: resource.keywords,
              quality_score: resource.quality_score,
              is_verified: false,
              is_active: true,
              target_audience: 'AI Generated',
              trade_type: resource.trade_type || ''
            });
        }
      }

      logger.info(`Saved ${resources.length} AI-generated resources to database`);
    } catch (error) {
      logger.error('Error saving AI resources to DB:', error);
    }
  }

  public async verifyURL(url: string): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      await fetch(url, {
        method: 'HEAD',
        signal: controller.signal,
        mode: 'no-cors'
      });

      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      logger.warn(`URL verification failed for ${url}:`, error);
      return false;
    }
  }

  public clearCache(): void {
    this.sessionCache.clear();
    logger.info('AI resource cache cleared');
  }
}

export const aiResourceGenerator = AIResourceGenerator.getInstance();
